<script setup>
import { computed, reactive, watch } from "vue";
import { useMutation } from "@tanstack/vue-query";
import axios from "axios";
import Swal from "sweetalert2";
import { errorToast, successToast } from "@/composables/useToasts";
import DefaultInput from "@/components/global-themed/inputs/DefaultInput.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import AlertBox from "@/components/global-themed/AlertBox.vue";

const props = defineProps({
  website: {
    type: Object,
    required: true,
  },
  locations: {
    type: Array,
    required: true,
  },
});

const form = reactive({
  uberall_business_id: props.website.uberall_business_id ?? '',
  locations: {},
});

watch(
  () => props.locations,
  (locations) => {
    form.locations = {};
    locations.forEach((location) => {
      form.locations[location.id] = location.uberall_location?.uberall_id ?? '';
    });
  },
  { immediate: true }
);

const goBack = () => {
  window.history.back();
};

const { mutate: saveSettings, isPending: isSaving, error: saveError } = useMutation({
  mutationFn: () => {
    return axios.post(
      `/ajax/v2/admin/website/${props.website.id}/uberall`,
      {
        uberall_business_id: form.uberall_business_id || null,
        locations: form.locations,
      }
    );
  },
  onSuccess: () => {
    successToast('Uberall settings saved successfully.');
  },
  onError: (error) => {
    if (error.response?.status !== 422) {
      errorToast('Something went wrong saving Uberall settings');
    }
  },
});

const formErrors = computed(() => {
  if (saveError.value?.response?.status === 422) {
    return saveError.value.response.data.errors || {};
  }
  return {};
});

const { mutateAsync: syncListings, isPending: isSyncing } = useMutation({
  mutationFn: () => axios.get(`/ajax/v2/admin/website/${props.website.id}/uberall/sync`),
  onSuccess: ({ data }) => {
    let successMessage = 'Uberall listings synced successfully, refreshing...';
    if (data?.success_message) {
      successMessage = data.success_message;
    }

    successToast(successMessage);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  },
  onError: (error) => {
    let errorMessage = 'Failed to sync Uberall listings';
    if (error.response?.data?.error_message) {
      errorMessage = error.response.data.error_message;
    }

    errorToast(errorMessage)
  },
});

const handleSyncListings = () => {
  Swal.fire({
    title: 'Confirm Auto-sync',
    text: 'I confirm this business\'s locations do not already exist in Uberall. If they do, they need linking manually.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#2F52E0',
    confirmButtonText: 'Proceed',
    cancelButtonText: 'Cancel',
    preConfirm: async () => await syncListings(),
  });
};
</script>

<template>
  <div class="tw-flex tw-justify-center">
    <div class="tw-w-full tw-max-w-5xl tw-mx-auto">
      <div class="tw-bg-white tw-border tw-border-neutral-200 tw-rounded-lg tw-mb-12 tw-shadow-sm">
        <div class="tw-px-5 tw-py-4 tw-border-b tw-border-neutral-200">
          <h4 class="tw-font-bold tw-text-lg tw-mb-0">
            Uberall Settings - {{ website.simple_url }}
          </h4>
        </div>
        <div class="tw-p-5">
          <AlertBox
            v-if="Object.keys(formErrors).length > 0"
            type="danger"
            class="tw-mb-5"
          >
            <div>
              Could not save due to errors, please check the fields below.
            </div>
          </AlertBox>

          <div class="tw-mb-5">
            <h5 class="tw-font-bold tw-mb-5">
              Website Business ID
            </h5>
            <DefaultInput
              v-model="form.uberall_business_id"
              :validation-errors="formErrors.uberall_business_id || []"
              placeholder="Enter Uberall Business ID"
            />
          </div>

          <hr class="tw-my-8">

          <h5 class="tw-font-bold tw-mb-5">
            Google Business Profile Locations
          </h5>

          <AlertBox
            v-if="locations.length === 0"
            type="info"
            class="tw-mb-5"
          >
            No GBP locations found for this website.
          </AlertBox>

          <div
            v-else
            class="tw-overflow-x-auto"
          >
            <table class="tw-w-full tw-border-collapse tw-border tw-border-neutral-200">
              <thead>
                <tr class="tw-bg-neutral-50">
                  <th class="tw-px-4 tw-py-3 tw-text-left tw-font-bold tw-border tw-border-neutral-200">
                    Location Name
                  </th>
                  <th class="tw-px-4 tw-py-3 tw-text-left tw-font-bold tw-border tw-border-neutral-200">
                    Address
                  </th>
                  <th class="tw-px-4 tw-py-3 tw-text-left tw-font-bold tw-border tw-border-neutral-200">
                    Uberall Location ID
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="location in locations"
                  :key="location.id"
                  class="tw-border-b tw-border-neutral-200"
                >
                  <td class="tw-px-4 tw-py-3 tw-border tw-border-neutral-200">
                    <strong>{{ location.title || location.name || 'N/A' }}</strong>
                  </td>
                  <td class="tw-px-4 tw-py-3 tw-border tw-border-neutral-200">
                    {{ location.short_address || 'N/A' }}
                  </td>
                  <td class="tw-px-4 tw-py-3 tw-border tw-border-neutral-200">
                    <DefaultInput
                      v-model="form.locations[location.id]"
                      :validation-errors="formErrors[`locations.${location.id}`] || formErrors['locations'] || []"
                      placeholder="Enter Uberall Location ID"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="tw-flex tw-justify-end tw-gap-3 tw-mt-6">
            <DefaultButton
              variant="primary-inverted"
              @click="goBack"
            >
              Back
            </DefaultButton>
            <DefaultButton
              variant="base"
              title="This will attempt to create or update Uberall with the location information automatically."
              :loading="isSyncing"
              :disabled="isSyncing || isSaving"
              @click="handleSyncListings"
            >
              Auto-sync
            </DefaultButton>
            <DefaultButton
              variant="primary"
              type="submit"
              :loading="isSaving"
              :disabled="isSaving || isSyncing"
              @click="saveSettings"
            >
              Save
            </DefaultButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
